import { useState, type Dispatch, type SetStateAction } from "react";
import { useTranslation } from "react-i18next";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AxiosError } from "axios";

import { Button, FileUploader, Input } from "@unified-checkout/design-system";

import { QUERY_KEYS } from "@app/dashboard/constants/query-keys";
import { respondToDispute } from "@app/dashboard/services/inner_layout/disputes/respond";
import { useLangStore } from "@app/dashboard/store/lang";
import type {
  IAcceptDrawerFormValues,
  TDrawer
} from "@app/dashboard/types/innerLayout/disputes";
import { notify } from "@app/dashboard/utils/notifier";
import {
  EVIDENCE_ALLOWED_FORMATS,
  TOASTER_SHARED_PROPS,
  sharedActionBtnsProps
} from "../../../../utils";
import styles from "./challenge-dispute.module.scss";


export const ChallengeDispute = ({
  disputeId,
  setIsDrawerOpen
}: {
  disputeId: string,
  setIsDrawerOpen: Dispatch<SetStateAction<Record<TDrawer, boolean>>>,
}) => {
  const { t } = useTranslation();
  const { lang } = useLangStore();
  const queryClient = useQueryClient();

  const [formValues, setFormValues] = useState<IAcceptDrawerFormValues>({
    files: null
  });

  const {
    isPending,
    mutate
  } = useMutation({
    mutationKey: [QUERY_KEYS.RESPOND_TO_DISPUTE],
    mutationFn: () => respondToDispute({
      disputeId,
      action: 'submit_evidence',
      evidence: {
        files: formValues.files ?? [],
        message: formValues.notes ?? ''
      }
    }),
    onSuccess: () => {

      notify.success(t('innerLayout.disputesPage.drawers.challenge.successMsg'), {
        ...TOASTER_SHARED_PROPS,
        onCloseHandler: () => {
          setIsDrawerOpen((prev) => ({ ...prev, challenge: false }));
          queryClient.removeQueries({ queryKey: [QUERY_KEYS.GET_SINGLE_DISPUTE, lang] });
        }
      });
    },
    onError: (err) => {
      const axiosErr = err as AxiosError;
      const msg = (axiosErr?.response?.data as any)?.message || t('errors.somethingWentWrong');

      notify.error(msg, TOASTER_SHARED_PROPS);
    },
  });

  const actionBtnsProps = [
    {
      ...sharedActionBtnsProps.secondary,
      text: t('form.back'),
      disabled: isPending,
      onClickHandler: () => setIsDrawerOpen((prev) => ({ ...prev, challenge: false }))
    },
    {
      ...sharedActionBtnsProps.main,
      text: t('innerLayout.disputesPage.actions.challenge'),
      loading: isPending,
      disabled: !formValues.files?.length,
      onClickHandler: () => mutate()
    },
  ];

  const handleFilesUpload = (files?: File[]) => {
    setFormValues(prev => ({
      ...prev,
      files: files ?? null
    }));
  };


  return (
    <>
      <div className={styles["challenge-dispute__file-uploader"]}>
        <div className={styles["challenge-dispute__file-uploader__title"]}>
          {t('innerLayout.disputesPage.drawers.challenge.fields.uploadEvidence.title')}
        </div>
        <FileUploader
          lang={lang}
          label={t('innerLayout.disputesPage.drawers.challenge.fields.uploadEvidence.label')}
          allowedFormats={EVIDENCE_ALLOWED_FORMATS}
          allowedSize={5}
          multiUpload
          onUploadHandler={handleFilesUpload as any}
        />
      </div>

      <div className={styles["challenge-dispute__additional-notes"]}>
        <div className={styles["challenge-dispute__additional-notes__title"]}>
          {t('innerLayout.disputesPage.drawers.challenge.fields.additionalNotes.title')}
        </div>

        <Input
          name="disputes-additional-notes"
          label={t('innerLayout.disputesPage.drawers.challenge.fields.additionalNotes.label')}
          placeholder={t('innerLayout.disputesPage.drawers.challenge.fields.additionalNotes.placeholder')}
          onChange={(e) => setFormValues(prev => ({ ...prev, notes: e.target.value }))}
          value={formValues.notes || ''}
        />
      </div>

      <div className="drawer-body__footer">
        {
          actionBtnsProps.map((btn, index) =>
            <Button {...btn} key={index} />
          )
        }
      </div>
    </>
  )
}
